/* global React */
/* ──────────────────────────────────────────────────────────────────────
   StagePage — /services/<stage-slug>/ detail page.

   Structure:
     · Right rail:   vertical lifecycle dots (fixed)
     · Hero (dark):  back link, big bg number, title, tagline, key-facts panel
     · Services (LIGHT #F8F8F8):  3-card grid with icons + deliverables
     · Stage nav (dark):  book-style prev / next stage links

   One template renders any of the 11 stages — content driven by
   window.DZ_SERVICES.stages, selected by window.STAGE_SLUG (set by the
   stage's index.html).
   ────────────────────────────────────────────────────────────────────── */

/* ── Service icons (id-keyed SVGs, monochrome, scale to currentColor) ── */
// Path-based AR localization for hardcoded stage-scaffolding labels (stage data from content/services_ar.js).
function dzIsAr() { return typeof window !== 'undefined' && window.location.pathname.startsWith('/ar/'); }
function dzT(en, ar) { return dzIsAr() ? ar : en; }

const STAGE_ICONS = {
  'shield-check': (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M12 3l9 4v6a9 9 0 0 1-9 9 9 9 0 0 1-9-9V7l9-4z" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  'stack': (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M12 3l9 5-9 5-9-5 9-5z" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M3 13l9 5 9-5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M3 18l9 5 9-5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  'plan': (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <rect x="4" y="4" width="16" height="18" rx="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M9 2v4M15 2v4M4 10h16M9 14h6M9 18h4" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  'cube': (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M12 3l9 5v8l-9 5-9-5V8l9-5z" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M3 8l9 5 9-5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 13v9" strokeLinecap="round"/>
    </svg>
  ),
  'compare': (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M3 6h7M3 12h5M3 18h7" strokeLinecap="round"/>
      <path d="M14 6h7M16 12h5M14 18h7" strokeLinecap="round"/>
      <path d="M12 3v18" strokeLinecap="round" strokeDasharray="2 3"/>
    </svg>
  ),
  'eye': (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="12" cy="12" r="3" strokeLinecap="round"/>
    </svg>
  )
};
function StageIcon({ id }) {
  return STAGE_ICONS[id] || STAGE_ICONS['plan'];
}

const { useState: useStageState, useEffect: useStageEffect } = React;

function StagePage() {
  const slug = window.STAGE_SLUG;
  const stages = window.DZ_SERVICES.stages;
  const stageIndex = stages.findIndex(s => s.slug === slug);
  const svcBase = dzIsAr() ? '/ar/services/' : '/services/';

  // If the URL has a hash like #service-eir, activate that service on
  // mount and after navigation. Falls back to the first service (0).
  function getActiveFromHash() {
    if (typeof window === 'undefined' || !window.location.hash) return 0;
    const m = window.location.hash.match(/^#service-(.+)$/);
    if (!m) return 0;
    if (stageIndex < 0) return 0;
    const i = stages[stageIndex].services.findIndex(sv => sv.slug === m[1]);
    return i >= 0 ? i : 0;
  }
  const [activeService, setActiveService] = useStageState(getActiveFromHash());

  // Re-sync if the hash changes after the page is already loaded
  // (browser back/forward, or another in-page hash link).
  useStageEffect(() => {
    function onHashChange() { setActiveService(getActiveFromHash()); }
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [stageIndex]);

  if (stageIndex < 0) {
    return (
      <main className="dz-stage dz-stage--missing">
        <div className="dz-stage__hero">
          <h1 className="dz-stage__hero-title">{dzT('Stage not found', 'المرحلة غير موجودة')}</h1>
          <p className="dz-stage__hero-tagline">
            The slug <code>{String(slug)}</code> doesn&apos;t match any stage.
          </p>
          <a href={svcBase} className="dz-btn dz-btn--aqua">{dzT('← Back to all services', 'العودة إلى كل الخدمات →')}</a>
        </div>
      </main>
    );
  }

  const stage = stages[stageIndex];
  const prevStage = stageIndex > 0 ? stages[stageIndex - 1] : null;
  const nextStage = stageIndex < stages.length - 1 ? stages[stageIndex + 1] : null;
  const totalStages = stages.length;

  // Service letter prefix: 01·A, 01·B, 01·C
  const letter = (i) => String.fromCharCode(65 + i);

  return (
    <main className="dz-stage" id="dz-main" aria-labelledby="stage-title">

      {/* ── Right rail: vertical lifecycle dots, fixed mid-screen ── */}
      <nav className="dz-stage__rail" aria-label="BIM lifecycle stages">
        <span className="dz-stage__rail-pos">
          <strong>{stage.number}</strong>
          <span>{String(totalStages).padStart(2, '0')}</span>
        </span>
        <ol className="dz-stage__rail-dots">
          {stages.map((s, i) => (
            <li key={s.id}>
              <a
                href={`${svcBase}${s.slug}/`}
                className={`dz-stage__rail-dot${i === stageIndex ? ' dz-stage__rail-dot--active' : ''}${i < stageIndex ? ' dz-stage__rail-dot--past' : ''}`}
                title={`${s.number} · ${s.label}`}
                aria-label={`${dzT('Stage', 'المرحلة')} ${s.number}: ${s.label}`}
                aria-current={i === stageIndex ? 'page' : undefined}
              >
                <span className="dz-stage__rail-dot-tip" aria-hidden="true">
                  {s.number} · {s.label}
                </span>
              </a>
            </li>
          ))}
        </ol>
      </nav>

      {/* ── Hero (dark): back link → big bg num + title + tagline + key facts ── */}
      <section className="dz-stage__hero">
        <a href={svcBase} className="dz-stage__back">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {dzT('All services', 'كل الخدمات')}
        </a>

        <span className="dz-stage__hero-bg-num" aria-hidden="true">{stage.number}</span>

        <div className="dz-stage__hero-content">
          <span className="dz-stage__hero-eyebrow">{dzT('Stage', 'المرحلة')} {stage.number}</span>
          <h1 id="stage-title" className="dz-stage__hero-title">{stage.label}</h1>
          <p className="dz-stage__hero-tagline">{stage.tagline}</p>
          <p className="dz-stage__hero-description">{stage.description}</p>

          {stage.keyFacts && (
            <div className="dz-stage__key-facts">
              {stage.keyFacts.map((f, i) => (
                <div key={i} className="dz-stage__key-fact">
                  <span className="dz-stage__key-fact-label">{f.label}</span>
                  <span className="dz-stage__key-fact-value">{f.value}</span>
                </div>
              ))}
            </div>
          )}

          {window.DZ_SERVICES.hub && window.DZ_SERVICES.hub.proof && (
            <p className="dz-stage__proof">{window.DZ_SERVICES.hub.proof}</p>
          )}
          <div className="dz-stage__hero-cta">
            <a className="dz-btn dz-btn--aqua" href="#inquiry">{dzT('Request a scope for this stage', 'اطلب نطاقاً لهذه المرحلة')}</a>
            <a className="dz-btn dz-btn--profile-outline" href="#stage-services">{dzT('See what we deliver', 'اطّلع على ما نُقدّمه')}</a>
          </div>
        </div>
      </section>

      {/* ── Services (LIGHT bg): interactive timeline — 3 nodes + active panel ── */}
      <section className="dz-stage__services" id="stage-services">
        <div className="dz-stage__services-inner">
          <span className="dz-eyebrow dz-eyebrow--on-light">{dzT('Three Services', 'ثلاث خدمات')}</span>
          <h2 className="dz-stage__services-title">
            {dzT('What we deliver in this ', 'ما نُقدّمه في هذه ')}<em>{dzT('stage', 'المرحلة')}</em>
          </h2>

          {/* Horizontal timeline — 3 clickable nodes */}
          <ol className="dz-svc-timeline" role="tablist" aria-label="Service navigator">
            {stage.services.map((svc, i) => (
              <li key={svc.id} id={`service-${svc.slug}`} className={`dz-svc-timeline__step${i === activeService ? ' dz-svc-timeline__step--active' : ''}${i < activeService ? ' dz-svc-timeline__step--past' : ''}`}>
                <button
                  type="button"
                  onClick={() => setActiveService(i)}
                  role="tab"
                  aria-selected={i === activeService}
                  className="dz-svc-timeline__btn"
                >
                  <span className="dz-svc-timeline__node" aria-hidden="true">
                    <span className="dz-svc-timeline__node-dot" />
                  </span>
                  <span className="dz-svc-timeline__num">{stage.number}·{letter(i)}</span>
                  <span className="dz-svc-timeline__short">{svc.shortName || svc.name}</span>
                </button>
              </li>
            ))}
          </ol>

          {/* Active service detail panel — re-keyed on change for fade-in.
              Always dark navy, "spotlight" effect on the light section bg. */}
          {(() => {
            const svc = stage.services[activeService];
            return (
              <article
                key={svc.id}
                className="dz-svc-detail"
                role="tabpanel"
                aria-label={`${svc.name} details`}
              >
                <div className="dz-svc-detail__head">
                  <div className="dz-svc-detail__icon">
                    <StageIcon id={svc.icon} />
                  </div>
                  <div className="dz-svc-detail__head-text">
                    <span className="dz-svc-detail__num">{stage.number}·{letter(activeService)}</span>
                    <h3 className="dz-svc-detail__name">{svc.name}</h3>
                  </div>
                  <div className="dz-svc-detail__meta">
                    {svc.standard && (
                      <span className="dz-svc-detail__badge">{svc.standard}</span>
                    )}
                    {svc.typicalDuration && (
                      <span className="dz-svc-detail__duration">{svc.typicalDuration}</span>
                    )}
                  </div>
                </div>

                <div className="dz-svc-detail__body">
                  <p className="dz-svc-detail__summary">{svc.summary}</p>
                  {svc.deliverables && svc.deliverables.length > 0 && (
                    <div className="dz-svc-detail__deliverables">
                      <span className="dz-svc-detail__deliverables-h">{(window.DZ_SERVICES && window.DZ_SERVICES.hub && window.DZ_SERVICES.hub.deliverablesLabel) || 'Deliverables'}</span>
                      <ul>
                        {svc.deliverables.map((d, di) => (
                          <li key={di}>{d}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </article>
            );
          })()}
          <p className="dz-stage__cta-link">
            {dzT('Need this stage on a major project? ', 'تحتاج هذه المرحلة في مشروع كبير؟ ')}
            <a href="#inquiry">{dzT('Request a scope', 'اطلب نطاقاً')}</a>
            {dzT(' and a senior BIM lead replies within 24 hours.', ' وسيرد عليك أحد كبار مختصّي BIM خلال 24 ساعة.')}
          </p>
        </div>
      </section>

      {/* ── Stage navigation (LIGHT bg, editorial "next chapter" treatment) ── */}
      <section className="dz-stage__nav-footer" aria-label="Adjacent stages">
        <div className="dz-stage__nav-inner">

          {/* Prev — small "back" link, secondary visual weight */}
          {prevStage ? (
            <a href={`${svcBase}${prevStage.slug}/`} className="dz-stage__back-card">
              <span className="dz-stage__back-card-eyebrow">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                  <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Previous · {prevStage.number}
              </span>
              <span className="dz-stage__back-card-name">{prevStage.label}</span>
            </a>
          ) : (
            <div className="dz-stage__back-card dz-stage__back-card--disabled">
              <span className="dz-stage__back-card-eyebrow">{dzT('First stage', 'المرحلة الأولى')}</span>
              <span className="dz-stage__back-card-name">{dzT('Where the BIM lifecycle begins', 'حيث تبدأ دورة حياة BIM')}</span>
            </div>
          )}

          {/* Next — large prominent card that reads like an editorial "Next chapter" */}
          {nextStage ? (
            <a href={`${svcBase}${nextStage.slug}/`} className="dz-stage__next-card">
              <div className="dz-stage__next-card-meta">
                <span className="dz-stage__next-card-eyebrow">{dzT('Continue to next stage', 'انتقل إلى المرحلة التالية')}</span>
                <span className="dz-stage__next-card-num">{nextStage.number}</span>
              </div>
              <h3 className="dz-stage__next-card-name">{nextStage.label}</h3>
              {nextStage.services && (
                <ul className="dz-stage__next-card-services">
                  {nextStage.services.map((svc) => (
                    <li key={svc.id}>{svc.name}</li>
                  ))}
                </ul>
              )}
              <span className="dz-stage__next-card-cta">
                Read stage {nextStage.number}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                  <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            </a>
          ) : (
            <div className="dz-stage__next-card dz-stage__next-card--disabled">
              <span className="dz-stage__next-card-eyebrow">{dzT('End of lifecycle', 'نهاية دورة الحياة')}</span>
              <h3 className="dz-stage__next-card-name">{dzT("You've reached the final stage", 'وصلت إلى المرحلة الأخيرة')}</h3>
              <a href={svcBase} className="dz-stage__next-card-cta">
                {dzT('Back to all stages', 'العودة إلى كل المراحل')}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                  <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
            </div>
          )}

        </div>
      </section>

    </main>
  );
}

window.StagePage = StagePage;
